import React, { useEffect, useState } from "react";
import { Button, Item, Loading, Select, Text, TextInput, IconButton, DatePicker, Divider } from "@ilc-technology/luik";
import { useDatasources } from "../../../contexts/StoryblokContext/StoryblokContext";
import { LabelKey } from "../../../Common/StoryblokTypes";
import { useSessionContext } from "../../../contexts/SessionContext/SessionContext";
import InformationBox from "../../InformationBox/InformationBox";
import { CustomTrackingEvent, trackEvent } from "../../../Common/services/Analytics";
import { convertToErrorDetails, getErrorDetailsToDisplay, logError } from "../../../Common/services/ErrorService";
import { ErrorCode, ErrorDetails } from "../../../Common/Types";
import { TRUE } from "../../../Common/Constants";
import { getCountryName } from "../../../Common/services/Countries";
import { Formik } from "formik";
import * as Yup from "yup";
import { CustomerServiceApi } from "../../../apis/customerServiceApi";
import IntlTelInput from "intl-tel-input/react";
import { Gender, RequestCustomerChange } from "../../../apis/generatedCustomerServiceApiClient";
import ErrorComponentWrapper from "../../ErrorHandling/ErrorComponentWrapper";
import TBSection from "../../BaseComponents/TBSection";

export type BasicStudentDetails = {
  firstName: string;
  lastName: string;
  middleName: string;
  email: string;
  country: string;
  mobilePhone: string;
  dateOfBirth: string;
  gender: string;
  nationality: string;

  // Only for markets with special characters
  firstNameLatin?: string | null;
  lastNameLatin?: string | null;
};

interface StudentDetailsProps {
  onStudentDetailsChange?: (newStudentDetails: BasicStudentDetails) => void;
  hasFlightArticle?: boolean;
}

const StudentDetails: React.FC<StudentDetailsProps> = ({ onStudentDetailsChange, hasFlightArticle }) => {
  const { labels, countries, featureSettings, nationalities } = useDatasources();
  const { session, language } = useSessionContext();
  const customerServiceApi = new CustomerServiceApi();

  const [isFormLoading, setIsFormLoading] = useState(false);
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(true);
  const [accountUpdateError, setAccountUpdateError] = useState<ErrorDetails | undefined>(undefined);
  const [initialValues, setInitialValues] = useState<BasicStudentDetails | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [loadingError, setLoadingError] = useState<ErrorDetails | undefined>(undefined);

  const getUsersFullDisplayName = (firstName: string, middleName: string, lastName: string) => {
    const nameParts = [firstName, featureSettings.isMiddleNameEnabled === TRUE ? middleName : "", lastName].filter(
      Boolean
    );
    return nameParts.join(" ");
  };

  const shouldRequestEnglishName = featureSettings.shouldRequestEnglishName === TRUE;

  const getCustomerInfo = () => {
    setIsLoading(true);
    customerServiceApi
      .getCustomer(session.accountUuid)
      .then(async (result) => {
        const studentDetails: BasicStudentDetails = {
          firstName: result.firstName ?? "",
          middleName: result.middleName ?? "",
          lastName: result.lastName ?? "",
          email: result.personDetails?.email ?? "",
          country: result.addresses?.mailingAddress?.countryCode ?? "",
          mobilePhone: result.personDetails?.mobilePhone ?? "",
          dateOfBirth: result.personDetails?.dateOfBirth ?? "",
          gender: result.personDetails?.gender ?? "",
          nationality: result.personDetails?.nationalityCountryCode ?? "",
          firstNameLatin: shouldRequestEnglishName ? result.latin?.firstName ?? null : null,
          lastNameLatin: shouldRequestEnglishName ? result.latin?.lastName ?? null : null,
        };
        setInitialValues(studentDetails);
        const isCountryMissing = !result.addresses?.mailingAddress?.countryCode;
        const isNationalityMissing = !result.personDetails?.nationalityCountryCode;
        setIsEditing(isCountryMissing || isNationalityMissing);
        if (onStudentDetailsChange) {
          onStudentDetailsChange(studentDetails);
        }
      })
      .catch((error) => {
        const errorDetails = convertToErrorDetails(error, ErrorCode.CustomerDetailsFetchFailed);
        if (errorDetails?.responseStatusCode != 404) {
          logError(errorDetails);
          setLoadingError(errorDetails);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getCustomerInfo();
  }, []);

  // The phone input is re-mounted with the saved value each time edit mode opens, so drop any stale invalid state.
  useEffect(() => {
    setIsPhoneNumberValid(true);
  }, [isEditing]);

  const handleSubmit = async (values: BasicStudentDetails) => {
    setIsFormLoading(true);
    setAccountUpdateError(undefined);

    const request: RequestCustomerChange = {
      firstName: values.firstName,
      middleName: values.middleName || undefined,
      lastName: values.lastName,
      latin: {
        firstName: shouldRequestEnglishName ? values.firstNameLatin ?? null : undefined,
        lastName: shouldRequestEnglishName ? values.lastNameLatin ?? null : undefined,
      },
      personDetails: {
        email: values.email,
        // Only send the phone when it was changed, so untouched (possibly legacy-format) values are never re-submitted.
        mobilePhone: values.mobilePhone !== initialValues?.mobilePhone ? values.mobilePhone || undefined : undefined,
        dateOfBirth: values.dateOfBirth,
        gender: values.gender as Gender,
        nationalityCountryCode: values.nationality,
      },
      addresses: {
        mailingAddress: {
          countryCode: values.country,
        },
      },
    };
    try {
      await customerServiceApi.updateCustomer(session.accountUuid, request);
      setIsEditing(false);
      trackEvent(session.opportunity.id, CustomTrackingEvent.SaveCompleteStudentDetailsPayment);
      setInitialValues(values);
      if (onStudentDetailsChange) {
        onStudentDetailsChange(values);
      }
    } catch (err: unknown) {
      setIsEditing(true);
      setAccountUpdateError(convertToErrorDetails(err, ErrorCode.AccountUpdateRequestFailed));
    } finally {
      setIsFormLoading(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getValidationSchema = (): any => {
    return Yup.object().shape({
      firstName: Yup.string().max(40, labels[LabelKey.valueTooLong]).required(labels[LabelKey.validationMessage]),
      middleName: Yup.string().max(40, labels[LabelKey.valueTooLong]).notRequired(),
      lastName: Yup.string().max(80, labels[LabelKey.valueTooLong]).required(labels[LabelKey.validationMessage]),
      email: Yup.string()
        .max(80, labels[LabelKey.valueTooLong])
        .email(labels[LabelKey.validationEmailMessage])
        .required(labels[LabelKey.validationMessage]),
      country: Yup.string().max(80, labels[LabelKey.valueTooLong]).required(labels[LabelKey.validationMessage]),
      nationality: Yup.string().max(80, labels[LabelKey.valueTooLong]).required(labels[LabelKey.validationMessage]),
      mobilePhone: Yup.string().max(40, labels[LabelKey.valueTooLong]).notRequired(),
      dateOfBirth: Yup.string().required(labels[LabelKey.validationMessage]),
      gender: Yup.string().required(labels[LabelKey.validationMessage]),
    });
  };

  const renderEditIcon = () => (
    <IconButton
      data-testid="edit-student-details"
      iconName={isEditing ? "close" : "crayon"}
      intent={isEditing ? "secondary-black" : "primary-black"}
      size="xs"
      aria-label="edit-student-details"
      onPress={() => {
        setIsEditing(!isEditing);
      }}
    />
  );

  return (
    <ErrorComponentWrapper isLoading={isLoading} error={loadingError}>
      {!isLoading && !loadingError && initialValues && (
        <div className="a-gap flex flex-col">
          <TBSection title={labels[LabelKey.studentDetails]}>
            {!hasFlightArticle && <div className="absolute right-0 top-0">{renderEditIcon()}</div>}
            <Text className="text-gray-45" variant="paragraph-body">
              {labels[LabelKey.studentDetailsDescription]}
            </Text>

            {hasFlightArticle && (
              <>
                <Divider />

                <div className="flex justify-between">
                  <Text variant="heading-5-bold">{labels[LabelKey.passportDetails]}</Text>
                  {renderEditIcon()}
                </div>
                <Text>{labels[LabelKey.passportDetailsDescription]}</Text>
              </>
            )}

            {!initialValues.country && !isEditing && (
              <div
                onClick={() => {
                  setIsEditing(!isEditing);
                }}
              >
                <InformationBox
                  intent="warning"
                  title={labels[LabelKey.missingDetails]}
                  content={labels[LabelKey.missingCountry]}
                  iconName={"alert-circle-outlined"}
                />
              </div>
            )}
            {!isEditing && countries && (
              <div className="flex flex-col">
                <div className="flex">
                  <Text data-testid="person-label" variant="paragraph-body-bold" className="mr-1">
                    {labels[LabelKey.fullName]}:
                  </Text>
                  <Text data-testid="person" variant="paragraph-body">
                    {getUsersFullDisplayName(initialValues.firstName, initialValues.middleName, initialValues.lastName)}
                  </Text>
                </div>
                {/* english name if feature flag shouldRequestEnglishName is true */}
                {shouldRequestEnglishName && (
                  <>
                    <div className="flex">
                      <Text data-testid="english-name" variant="paragraph-body-bold" className="mr-1">
                        {labels[LabelKey.firstNameLatin]}:
                      </Text>
                      <Text data-testid="english-name-value" variant="paragraph-body">
                        {initialValues.firstNameLatin}
                      </Text>
                    </div>
                    <div className="flex">
                      <Text data-testid="english-name" variant="paragraph-body-bold" className="mr-1">
                        {labels[LabelKey.lastNameLatin]}:
                      </Text>
                      <Text data-testid="english-name-value" variant="paragraph-body">
                        {initialValues.lastNameLatin}
                      </Text>
                    </div>
                  </>
                )}

                <div className="flex">
                  <Text data-testid="gender" variant="paragraph-body-bold" className="mr-1">
                    {labels[LabelKey.gender]}:
                  </Text>
                  <Text data-testid="gender-value" variant="paragraph-body">
                    {labels["gender_" + initialValues.gender]}
                  </Text>
                </div>
                <div className="flex">
                  <Text data-testid="dateOfBirth" variant="paragraph-body-bold" className="mr-1">
                    {labels[LabelKey.dateOfBirth]}:
                  </Text>
                  <Text data-testid="dateOfBirth-value" variant="paragraph-body">
                    {initialValues.dateOfBirth}
                  </Text>
                </div>
                <div className="flex">
                  <Text data-testid="email" variant="paragraph-body-bold" className="mr-1">
                    {labels[LabelKey.email]}:
                  </Text>
                  <Text data-testid="email-value" variant="paragraph-body">
                    {initialValues.email}
                  </Text>
                </div>
                <div className="flex">
                  <Text data-testid="country" variant="paragraph-body-bold" className="mr-1">
                    {labels[LabelKey.country]}:
                  </Text>
                  <Text data-testid="country-value" variant="paragraph-body">
                    {getCountryName(initialValues.country, language, countries[initialValues.country])}
                  </Text>
                </div>
                <div className="flex">
                  <Text data-testid="nationality" variant="paragraph-body-bold" className="mr-1">
                    {labels[LabelKey.countryOfNationality]}:
                  </Text>
                  <Text data-testid="nationality-value" variant="paragraph-body">
                    {getCountryName(initialValues.nationality, language, initialValues.nationality)}
                  </Text>
                </div>
                <div className="flex">
                  <Text data-testid="mobilePhone" variant="paragraph-body-bold" className="mr-1">
                    {labels[LabelKey.phoneNumber]}:
                  </Text>
                  <Text data-testid="mobilePhone-value" variant="paragraph-body">
                    {initialValues.mobilePhone}
                  </Text>
                </div>
              </div>
            )}
            {isEditing && (
              <>
                <Formik
                  initialValues={initialValues}
                  validationSchema={getValidationSchema()}
                  validateOnChange={true}
                  validateOnMount={true}
                  onSubmit={handleSubmit}
                >
                  {({
                    setFieldValue,
                    isSubmitting,
                    handleSubmit,
                    dirty,
                    values,
                    handleBlur,
                    handleChange,
                    touched,
                    errors,
                    isValid,
                  }) => (
                    <form onSubmit={handleSubmit}>
                      <div className="a-gap-sm flex flex-col">
                        <TextInput
                          data-testid="firstName"
                          aria-label="firstName"
                          isRequired={true}
                          name="firstName"
                          label={labels[LabelKey.firstName]}
                          placeholder={labels[LabelKey.firstName]}
                          type="text"
                          value={values.firstName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isDisabled={isFormLoading || isSubmitting}
                          isInvalid={errors.firstName != null}
                          validationState={errors.firstName ? "invalid" : "valid"}
                          errorMessage={errors.firstName}
                          touched={touched.firstName}
                          autoComplete="given-name"
                        />
                        {featureSettings.isMiddleNameEnabled === TRUE && (
                          <TextInput
                            aria-label="middleName"
                            data-testid="middleName"
                            name="middleName"
                            label={labels[LabelKey.middleName]}
                            placeholder={labels[LabelKey.middleName]}
                            type="text"
                            value={values.middleName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isDisabled={isFormLoading}
                            isInvalid={errors.middleName != null}
                            errorMessage={errors.middleName}
                            touched={touched.middleName}
                            autoComplete="additional-name"
                          />
                        )}
                        <TextInput
                          data-testid="lastName"
                          aria-label="lastName"
                          isRequired={true}
                          name="lastName"
                          label={labels[LabelKey.lastName]}
                          placeholder={labels[LabelKey.lastName]}
                          type="text"
                          value={values.lastName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isDisabled={isFormLoading || isSubmitting}
                          isInvalid={errors.lastName != null}
                          validationState={errors.lastName ? "invalid" : "valid"}
                          errorMessage={errors.lastName}
                          touched={touched.lastName}
                          autoComplete="family-name"
                        />
                        {shouldRequestEnglishName && (
                          <>
                            <TextInput
                              data-testid="firstNameLatin"
                              aria-label="firstNameLatin"
                              isRequired={true}
                              name="firstNameLatin"
                              label={labels[LabelKey.firstNameLatin]}
                              placeholder={labels[LabelKey.firstNameLatin]}
                              type="text"
                              value={values.firstNameLatin}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isDisabled={isFormLoading || isSubmitting}
                              isInvalid={errors.firstNameLatin != null}
                              validationState={errors.firstNameLatin ? "invalid" : "valid"}
                              errorMessage={errors.firstNameLatin}
                              touched={touched.firstNameLatin}
                              autoComplete="family-name"
                            />
                            <TextInput
                              data-testid="lastNameLatin"
                              aria-label="lastNameLatin"
                              isRequired={true}
                              name="lastNameLatin"
                              label={labels[LabelKey.lastNameLatin]}
                              placeholder={labels[LabelKey.lastNameLatin]}
                              type="text"
                              value={values.lastNameLatin}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isDisabled={isFormLoading || isSubmitting}
                              isInvalid={errors.lastNameLatin != null}
                              validationState={errors.lastNameLatin ? "invalid" : "valid"}
                              errorMessage={errors.lastNameLatin}
                              touched={touched.lastNameLatin}
                            />
                          </>
                        )}
                        <Select
                          name="gender-select"
                          label={labels[LabelKey.gender]}
                          defaultSelectedKey={values.gender}
                          validationState={errors.gender ? "invalid" : "valid"}
                          errorMessage={errors.gender}
                          className="my-0"
                          trackingInfo="track-select"
                          isRequired={true}
                          onSelectionChange={async (value: string) => await setFieldValue("gender", value)}
                          disabled={isFormLoading || isSubmitting}
                          touched={touched.gender}
                          onBlur={handleBlur}
                          autoComplete="sex"
                        >
                          {["Female", "Male", "Other"].map((genderOption) => (
                            <Item key={genderOption} value={genderOption}>
                              {labels["gender_" + genderOption] ?? genderOption}
                            </Item>
                          ))}
                        </Select>
                        <DatePicker
                          data-testid="dateOfBirth-picker"
                          label={labels[LabelKey.dateOfBirth]}
                          errorMessage={errors.dateOfBirth}
                          validationState={errors.dateOfBirth ? "invalid" : "valid"}
                          name="dateOfBirth"
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            handleChange(e);
                          }}
                          required={true}
                          value={values.dateOfBirth}
                          disabled={isFormLoading || isSubmitting}
                          onBlur={handleBlur}
                          touched={touched.dateOfBirth}
                          autoComplete="bday"
                        />
                        <TextInput
                          data-testid="email-edit"
                          aria-label="email"
                          isRequired={true}
                          name="email"
                          label={labels[LabelKey.email]}
                          placeholder={labels[LabelKey.email]}
                          type="email"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isDisabled={isFormLoading || isSubmitting}
                          isInvalid={errors.email != null}
                          validationState={errors.email ? "invalid" : "valid"}
                          errorMessage={errors.email}
                          touched={touched.email}
                          autoComplete="email"
                        />
                        <div data-testid="country-select">
                          <Select
                            aria-label="country-select"
                            name="country-select"
                            label={labels[LabelKey.country]}
                            className="mb-4 mt-0"
                            defaultSelectedKey={values.country}
                            trackingInfo="track-select"
                            isRequired={true}
                            onSelectionChange={async (value: string) =>
                              await setFieldValue("country", value.toString())
                            }
                            disabled={isFormLoading}
                            onBlur={handleBlur}
                            validationState={errors.country ? "invalid" : "valid"}
                            errorMessage={errors.country}
                            touched={touched.country}
                            autoComplete="country"
                          >
                            {Object.entries(countries).map(([countryCode, countryName]) => (
                              <Item key={countryCode} value={countryCode}>
                                {getCountryName(countryCode, language, countryName)}
                              </Item>
                            ))}
                          </Select>
                        </div>
                        <div data-testid="nationality-select">
                          <Select
                            aria-label="nationality-select"
                            name="nationality-select"
                            label={labels[LabelKey.countryOfNationality]}
                            className="mb-4 mt-0"
                            defaultSelectedKey={values.nationality}
                            trackingInfo="track-select"
                            isRequired={true}
                            onSelectionChange={async (value: string) =>
                              await setFieldValue("nationality", value.toString())
                            }
                            disabled={isFormLoading}
                            onBlur={handleBlur}
                            validationState={errors.nationality ? "invalid" : "valid"}
                            errorMessage={errors.nationality}
                            touched={touched.nationality}
                            autoComplete="country"
                          >
                            {Object.entries(nationalities).map(([nationalityName, nationalityCode]) => (
                              <Item key={nationalityCode} value={nationalityCode}>
                                {/* We use getCountryName, as there is no JS native way to get demonym for a country
                              we can use Intl.DisplayNames(languageCode, { type: "language" }), but that's missing more than 50% of the nationalities we have. */}
                                {getCountryName(nationalityCode, language, nationalityName)}
                              </Item>
                            ))}
                          </Select>
                        </div>
                        <div
                          className={`tel-container a-rounded flex flex-col justify-center border border-neutral-300
                    ${(errors.mobilePhone && touched.mobilePhone) || (touched.mobilePhone && !isPhoneNumberValid) ? "tel-invalid" : ""}
                    ${isFormLoading || isSubmitting ? "tel-container-disabled" : ""}`}
                        >
                          <IntlTelInput
                            initialValue={values.mobilePhone}
                            onChangeValidity={(value: boolean) => {
                              setIsPhoneNumberValid(value);
                            }}
                            onChangeNumber={async (value) => {
                              await setFieldValue("mobilePhone", value, true);
                            }}
                            usePreciseValidation={true}
                            initOptions={{
                              utilsScript: "https://cdn.jsdelivr.net/npm/intl-tel-input@21.1.1/build/js/utils.js",
                              nationalMode: false,
                              strictMode: false,
                              formatAsYouType: true,
                              customPlaceholder: function () {
                                return labels[LabelKey.phoneNumber];
                              },
                              initialCountry: "auto",
                              geoIpLookup: (success) => {
                                fetch("https://ipapi.co/json")
                                  .then((res) => res.json())
                                  .then((data) => success(data.country_code))
                                  .catch(() => {});
                              },
                            }}
                            inputProps={{
                              onBlur: handleBlur("mobilePhone"),
                              "data-testid": "mobilePhone-input",
                              name: "mobilePhone-input",
                              disabled: isFormLoading || isSubmitting,
                            }}
                          />
                        </div>
                      </div>
                      {accountUpdateError && (
                        <InformationBox
                          intent="alert"
                          title={labels[LabelKey.saveFailed]}
                          content={labels[LabelKey.saveFailedDescription]}
                          iconName={"alert-circle-outlined"}
                          additionalInfo={getErrorDetailsToDisplay(accountUpdateError)}
                          testid="account-update-error"
                        />
                      )}
                      <div className="float-right mt-8 flex flex-row items-center gap-6">
                        <Button
                          aria-label="cancel-button"
                          data-testid="cancel-button"
                          intent="secondary-black"
                          size="base"
                          onPress={() => {
                            setIsEditing(!isEditing);
                            setAccountUpdateError(undefined);
                          }}
                          isDisabled={isFormLoading || isSubmitting}
                        >
                          {labels[LabelKey.cancel]}
                        </Button>
                        {isFormLoading || isSubmitting ? (
                          <div className="a-save-button save-details-loading">
                            <Loading className="loading save-details-loading-spinner" size="lg" />
                          </div>
                        ) : (
                          <input
                            aria-label="save-details-button"
                            data-testid="save-details-button"
                            className="a-save-button"
                            type="submit"
                            // mobilePhone is optional (see validationSchema): an empty number must never block
                            // save just because the phone widget flags "" as invalid.
                            disabled={
                              isFormLoading ||
                              isSubmitting ||
                              !dirty ||
                              !isValid ||
                              (!!values.mobilePhone && !isPhoneNumberValid)
                            }
                            value={labels[LabelKey.save]}
                          />
                        )}
                      </div>
                    </form>
                  )}
                </Formik>
              </>
            )}
          </TBSection>
        </div>
      )}
    </ErrorComponentWrapper>
  );
};

export default StudentDetails;
